import { useNavigate } from 'react-router-dom';
import {
  ArrowRightLeft,
  BarChart3,
  ClipboardCheck,
  CodeXml,
  Database,
  LayoutDashboard,
  LayoutGrid,
  Network,
  Settings,
  SlidersHorizontal,
  TrendingUp,
  User,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useWorkspaceSidebar } from '@/components/layout/WorkspaceSidebarContext';
import { useSettingsOptional } from '@/components/layout/SettingsContext';
import SidebarUserMenu from '@/components/layout/SidebarUserMenu';
import WorkspaceSidebarLogo from '@/components/layout/WorkspaceSidebarLogo';
import {
  WORKSPACE_SIDEBAR_BG_CLASS,
  WORKSPACE_SIDEBAR_REVEAL_CLASS,
  workspaceSidebarNavRowClass,
} from '@/components/layout/workspace-layout';

interface NavItem {
  key: string;
  label: string;
  icon: LucideIcon;
}

const CODE_ANALYSIS_ITEMS: NavItem[] = [
  { key: '2-0', label: 'Inventory', icon: Database },
  { key: '2-1', label: 'Build Analysis', icon: BarChart3 },
  { key: '2-2', label: 'Scripts Inventory', icon: CodeXml },
  { key: '2-3', label: 'Call Graph', icon: Network },
];

const COVERAGE_ITEMS: NavItem[] = [
  { key: '3-0-0', label: 'Combined', icon: LayoutDashboard },
  { key: '3-0-1', label: 'Manual', icon: User },
  { key: '3-0-2', label: 'Automation', icon: SlidersHorizontal },
  { key: '3-0-3', label: 'Coverage Trend', icon: TrendingUp },
  { key: '3-1', label: 'Test Gap Analysis', icon: ArrowRightLeft },
];

const TESTING_ITEMS: NavItem[] = [
  { key: '4', label: 'Manual Testing', icon: ClipboardCheck },
];

/** Divider on the collapsed rail; section title when the sidebar is expanded. */
const SidebarSection = ({ title }: { title: string }) => (
  <div className="relative my-1 flex h-4 w-full shrink-0 items-center">
    <div
      className="ml-1.5 h-px w-6 shrink-0 bg-[var(--inv-sidebar-divider)] transition-opacity duration-150 min-[901px]:group-hover/sidebar:opacity-0 min-[901px]:group-has-[:focus-visible]/sidebar:opacity-0"
      aria-hidden
    />
    <span
      className={cn(
        'absolute left-1.5 whitespace-nowrap text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-[var(--inv-sidebar-icon)]',
        WORKSPACE_SIDEBAR_REVEAL_CLASS,
      )}>
      {title}
    </span>
  </div>
);

const SidebarIconButton = ({
  label,
  icon: Icon,
  active,
  onClick,
}: {
  label: string;
  icon: LucideIcon;
  active?: boolean;
  onClick: () => void;
}) => (
  <Button
    type="button"
    variant="ghost"
    className={workspaceSidebarNavRowClass(Boolean(active))}
    aria-label={label}
    aria-current={active ? 'page' : undefined}
    onClick={onClick}>
    <span className="flex h-9 w-9 shrink-0 items-center justify-center">
      <Icon className="h-[1.125rem] w-[1.125rem]" strokeWidth={1.75} aria-hidden />
    </span>
    <span
      className={cn(
        'min-w-0 truncate pr-3 text-[0.8125rem] font-medium',
        WORKSPACE_SIDEBAR_REVEAL_CLASS,
      )}
      aria-hidden>
      {label}
    </span>
  </Button>
);

const SidebarNavigation = ({
  id,
  handleSideBarCallback,
}: {
  id: string;
  handleSideBarCallback: (key: string) => void;
}) => {
  const navigate = useNavigate();
  const { closeMobile } = useWorkspaceSidebar() ?? {};
  const settings = useSettingsOptional();

  const onNav = (fn: () => void) => {
    fn();
    closeMobile?.();
  };

  const renderGroup = (items: NavItem[]) =>
    items.map(item => (
      <SidebarIconButton
        key={item.key}
        label={item.label}
        icon={item.icon}
        active={id === item.key}
        onClick={() => onNav(() => handleSideBarCallback(item.key))}
      />
    ));

  return (
    <aside
      className={cn(
        'box-border flex h-full min-h-0 w-full flex-col items-stretch',
        WORKSPACE_SIDEBAR_BG_CLASS,
      )}
      aria-label="Workspace navigation">
      <WorkspaceSidebarLogo />

      <nav className="flex min-h-0 w-full flex-1 flex-col items-stretch overflow-x-hidden overflow-y-auto px-[0.875rem] pb-3">
        <SidebarIconButton
          label="Components"
          icon={LayoutGrid}
          onClick={() => onNav(() => navigate('/sia/components'))}
        />

        <SidebarSection title="Code Analysis" />
        {renderGroup(CODE_ANALYSIS_ITEMS)}

        <SidebarSection title="Coverage Analysis" />
        {renderGroup(COVERAGE_ITEMS)}

        <SidebarSection title="Testing" />
        {renderGroup(TESTING_ITEMS)}
      </nav>

      <div className="flex w-full shrink-0 flex-col items-stretch gap-2 px-[0.875rem] pb-4 pt-2">
        <SidebarIconButton
          label="Settings"
          icon={Settings}
          onClick={() => {
            closeMobile?.();
            settings?.openSettings();
          }}
        />
        <SidebarUserMenu />
      </div>
    </aside>
  );
};

export default SidebarNavigation;
